import { call } from '@advanza/api'
import { Checkbox } from '@advanza/input'
import Modal from '@advanza/modal'
import { Button, PreIcon } from '@advanza/ui'
import LoadingDots from '@trustoo/components/ui/LoadingDots'
import { QuestionType } from '@trustoo/enums/QuestionType'
import RequestDetailsContainer from 'containers/RequestDetailsContainer'
import { Request } from 'hooks/requestHooks'
import { RequestEntities, ServiceEntities } from 'interfaces/RequestEntities'
import Service from 'interfaces/Services'
import { useMemo, useState } from 'react'

interface RequestQuestion {
    question_id: number
    value: string
}

interface RequestFile {
    id?: number
    name?: string
}

interface ExtendedRequest extends Partial<Request> {
    requests_questions?: RequestQuestion[]
    requests_dates?: number[]
    requests_months?: number[]
    requests_moments?: number[]
    files?: RequestFile[]
}

interface RequestMonth {
    question_id: number
    month: string
}

interface RequestMoment {
    question_id: number
    day_of_the_week: number
    part_of_the_day: number
}

interface ExtendedRequestEntities extends RequestEntities {
    requestsMonths?: Record<number, RequestMonth>
    requestsMoments?: Record<number, RequestMoment>
}

interface GeneratedResult {
    message: string
    input_tokens: number
    output_tokens: number
}

interface AnsweredRow {
    questionId: number
    label: string
    answerSummary: string
}

interface Props {
    requestId: number
    request?: ExtendedRequest
    service?: Partial<Service>
    requestEntities?: ExtendedRequestEntities
    serviceEntities?: ServiceEntities
}

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
const PARTS = ['morning', 'afternoon', 'evening']

const RequestEnrichmentMessageTestButton = ({
    requestId,
    request,
    service,
    requestEntities,
    serviceEntities,
}: Props) => {
    const [open, setOpen] = useState(false)
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
    const [isGenerating, setIsGenerating] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [result, setResult] = useState<GeneratedResult | null>(null)

    const answeredRows = useMemo<AnsweredRow[]>(() => {
        if (!request || !service?.questions) return []

        const valuesByQuestionId = new Map<number, string[]>()
        const pushValue = (questionId: number | undefined, value: string | undefined) => {
            if (!questionId || !value) return
            const existing = valuesByQuestionId.get(questionId) ?? []
            existing.push(value)
            valuesByQuestionId.set(questionId, existing)
        }

        request.requests_options?.forEach((opt) => pushValue(opt.question_id, opt.value))
        request.requests_questions?.forEach((rq) => pushValue(rq.question_id, rq.value))

        request.requests_dates?.forEach((dateId) => {
            const d = requestEntities?.requestsDates?.[dateId]
            pushValue(d?.question_id, d?.exec_date)
        })
        request.requests_months?.forEach((monthId) => {
            const m = requestEntities?.requestsMonths?.[monthId]
            pushValue(m?.question_id, m?.month)
        })
        request.requests_moments?.forEach((momentId) => {
            const moment = requestEntities?.requestsMoments?.[momentId]
            if (!moment?.question_id) return
            const day = DAYS[moment.day_of_the_week - 1] ?? `day ${moment.day_of_the_week}`
            const part = PARTS[moment.part_of_the_day - 1] ?? `part ${moment.part_of_the_day}`
            pushValue(moment.question_id, `${day} ${part}`)
        })

        const fileQuestionId = request.files?.length
            ? (service.questions.find(
                  (id) => serviceEntities?.questions?.[id]?.type === QuestionType.FILE,
              ) ?? null)
            : null

        if (fileQuestionId !== null) {
            pushValue(fileQuestionId, `${request.files?.length ?? 0} file(s) uploaded`)
        }

        return service.questions.flatMap((questionId) => {
            const values = valuesByQuestionId.get(questionId)
            if (!values?.length) return []
            const meta = serviceEntities?.questions?.[questionId]
            const label = meta?.question_name_filter || meta?.question || `#${questionId}`
            return [{ questionId, label, answerSummary: values.join(', ') }]
        })
    }, [request, service, requestEntities, serviceEntities])

    const toggle = (questionId: number) => {
        setSelectedIds((prev) => {
            const next = new Set(prev)
            if (next.has(questionId)) {
                next.delete(questionId)
            } else {
                next.add(questionId)
            }
            return next
        })
    }

    const close = () => {
        setOpen(false)
        setResult(null)
        setError(null)
        setSelectedIds(new Set())
    }

    const generate = () => {
        setIsGenerating(true)
        setError(null)
        setResult(null)
        call(`office/requests/test-enrichment-message/${requestId}`, {
            json: { newQuestionIds: Array.from(selectedIds) },
        })
            .then((response: GeneratedResult) => {
                setResult(response)
            })
            .catch((err: { error?: string } | Error) => {
                const msg = err instanceof Error ? err.message : err?.error || 'Unknown error'
                setError(msg)
            })
            .finally(() => setIsGenerating(false))
    }

    return (
        <div>
            <Button name="text" onClick={() => setOpen(true)}>
                <PreIcon primColor icon="auto_awesome">
                    Test enrichment message
                </PreIcon>
            </Button>
            <Modal open={open} close={close}>
                <div className="p-4">
                    <h3 className="mb-2 text-xl font-semibold">
                        Test AI enrichment follow-up message
                    </h3>
                    <p className="mb-4 text-md text-neutral-600">
                        Tick the questions you want to mark as &quot;newly enriched&quot; and click
                        Generate. The AI will draft a short message from the consumer to the matched
                        providers, focused on the ticked answers.
                    </p>

                    {answeredRows.length === 0 ? (
                        <p className="text-md text-neutral-500">
                            No answered questions on this request.
                        </p>
                    ) : (
                        <div className="mb-4 max-h-72 overflow-auto border border-neutral-200 rounded">
                            {answeredRows.map((row) => (
                                <div
                                    key={row.questionId}
                                    className="px-3 py-2 border-b border-neutral-100 last:border-b-0">
                                    <Checkbox
                                        top
                                        className="[&_*>i]:overflow-visible"
                                        value={selectedIds.has(row.questionId)}
                                        onChange={() => toggle(row.questionId)}>
                                        <div className="text-md">
                                            <div className="font-medium">{row.label}</div>
                                            <div className="text-neutral-600">
                                                {row.answerSummary}
                                            </div>
                                        </div>
                                    </Checkbox>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="flex items-center gap-3">
                        <Button name="primary" onClick={generate} disabled={isGenerating}>
                            {isGenerating ? (
                                <span>
                                    <LoadingDots /> generating
                                </span>
                            ) : (
                                'Generate'
                            )}
                        </Button>
                        <span className="text-sm text-neutral-500">
                            {selectedIds.size} marked as new
                        </span>
                    </div>

                    {error && (
                        <div className="mt-4 p-3 bg-red-50 border border-red-200 text-red-800 text-md rounded">
                            {error}
                        </div>
                    )}

                    {result && (
                        <div className="mt-4">
                            <div className="text-sm text-neutral-500 mb-1">
                                tokens in / out: {result.input_tokens} / {result.output_tokens}
                            </div>
                            <div className="p-3 bg-neutral-50 border border-neutral-200 rounded whitespace-pre-wrap text-md">
                                {result.message}
                            </div>
                        </div>
                    )}
                </div>
            </Modal>
        </div>
    )
}

export default RequestDetailsContainer(RequestEnrichmentMessageTestButton)
